/** Parses API/Go envelopes, toasts failures unless silent, and throws non-2xx responses. */

import { message } from '@/components/Toast'

export type ApiRequestOptions = {
  /** When true, do not auto-toast server-reported errors for this call. */
  silent?: boolean
}

/**
 * Failure thrown for a non-2xx response. `code` is the envelope's own code:
 * numeric for Go_Process envelopes, a string identifier for Gateway failures.
 */
export type ApiRequestError = Error & {
  status?: number
  code?: number | string
}

/** Go_Process envelope accepts legacy upper/lower-case fields. */
type Envelope = {
  code?: number | string
  Code?: number | string
  success?: boolean
  Success?: boolean
  message?: string
  Message?: string
  text?: string
  Text?: string
}

function isEnvelope(value: unknown): value is Envelope {
  if (!value || typeof value !== 'object') return false
  const record = value as Record<string, unknown>
  return (
    'code' in record ||
    'Code' in record ||
    'success' in record ||
    'Success' in record ||
    'message' in record ||
    'Message' in record ||
    'text' in record ||
    'Text' in record
  )
}

function isEnvelopeSuccess(envelope: Envelope): boolean {
  // Explicit success flag wins if it's set.
  if (envelope.success === true || envelope.Success === true) return true
  if (envelope.success === false || envelope.Success === false) return false
  // Fall back to numeric code: 0 = ok (Go convention), anything else = error.
  const code = envelope.code ?? envelope.Code
  if (typeof code === 'number') return code === 0
  // No success signal at all — assume success so we don't false-positive.
  return true
}

function extractMessage(envelope: Envelope): string {
  const top = (envelope.message || envelope.Message || envelope.text || envelope.Text || '').trim()
  if (top) return top
  // Some send endpoints report failures only in results[]; surface those reasons.
  const record = envelope as unknown as Record<string, unknown>
  const results = record.results
  if (Array.isArray(results)) {
    const reasons: string[] = []
    const seen = new Set<string>()
    for (const entry of results) {
      if (!entry || typeof entry !== 'object') continue
      const row = entry as Record<string, unknown>
      if (row.success === true) continue
      const msg = typeof row.message === 'string' ? row.message.trim() : ''
      if (!msg) continue
      const target = typeof row.target === 'string' ? row.target : ''
      const key = target ? `${target}::${msg}` : msg
      if (seen.has(key)) continue
      seen.add(key)
      reasons.push(target ? `${target}: ${msg}` : msg)
    }
    if (reasons.length) {
      return reasons.length <= 2
        ? reasons.join('; ')
        : `${reasons.slice(0, 2).join('; ')}（共 ${reasons.length} 项失败）`
    }
  }
  return ''
}

/** Dispatch only on explicit Go `Code: -13`; text-only failures are ambiguous. */
export const SESSION_EXPIRED_EVENT = 'wxproto-desk:session-expired'

/** Go_Process well-known error code for "当前 wxid 已退出 / session 已失效". */
const SESSION_EXPIRED_CODE = -13

/** Dispatched on console-cookie 401s; distinct from WeChat protocol expiry. */
export const CONSOLE_SESSION_EXPIRED_EVENT = 'wxproto-desk:console-session-expired'

// Many parallel requests fail together when the cookie dies; throttle so the
// user sees one toast and one logout transition, not a storm.
const CONSOLE_SESSION_EXPIRED_THROTTLE_MS = 5_000
let consoleSessionExpiredNotifiedAt = 0

function dispatchConsoleSessionExpired(): void {
  const now = Date.now()
  if (now - consoleSessionExpiredNotifiedAt < CONSOLE_SESSION_EXPIRED_THROTTLE_MS) return
  consoleSessionExpiredNotifiedAt = now
  message.error('控制台登录已过期，请重新登录')
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CONSOLE_SESSION_EXPIRED_EVENT))
  }
}

/** Parse API envelopes, toast failures, and dispatch explicit session expiry. */
function handleEnvelope<T>(body: T, silent: boolean | undefined, path: string): T {
  if (!isEnvelope(body)) return body
  if (isEnvelopeSuccess(body)) return body

  const code = body.code ?? body.Code
  if (code === SESSION_EXPIRED_CODE) {
    // Session expired. Always surface this one even when silent was
    // requested — the user can't do anything else until they re-login.
    message.error('当前账号登录已退出，请重新登录')
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT))
    }
    return body
  }

  if (silent) return body
  const reason = extractMessage(body) || `请求失败（${path}）`
  message.error(reason)
  return body
}

async function parseResponseBody(response: Response): Promise<unknown> {
  const text = await response.text()
  if (!text) return {}
  try {
    return JSON.parse(text)
  } catch {
    // Preserve the original text so the caller can still see what came back.
    return text
  }
}

async function requestJson<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  path: string,
  body: unknown,
  options: ApiRequestOptions | undefined,
): Promise<T> {
  const hasJsonBody = (method === 'POST' || method === 'PUT' || method === 'PATCH') && body !== undefined
  let response: Response
  try {
    response = await fetch(`/api${path}`, {
      method,
      headers: hasJsonBody
        ? { 'Content-Type': 'application/json', Accept: 'application/json' }
        : method === 'GET'
          ? { Accept: 'application/json' }
          : undefined,
      body: hasJsonBody ? JSON.stringify(body) : undefined,
      cache: method === 'GET' ? 'no-store' : undefined,
    })
  } catch (err) {
    // Network / CORS / abort — no envelope to parse, fail loudly.
    const reason = err instanceof Error ? err.message : String(err)
    if (!options?.silent) message.error(`网络异常：${reason}`)
    throw err instanceof Error ? err : new Error(reason)
  }

  const parsed = await parseResponseBody(response)

  if (!response.ok) {
    // Check explicit `Code: -13` before generic HTTP handling; text alone is ambiguous.
    if (isEnvelope(parsed)) {
      const code = parsed.code ?? parsed.Code
      if (code === SESSION_EXPIRED_CODE) {
        message.error('当前账号登录已退出，请重新登录')
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT))
        }
        throw new Error('session expired')
      }
    }
    // Console session gone (cookie expired / revoked). /auth/* is excluded:
    // its 401s are part of the normal login/bootstrap flow, not an expiry.
    if (response.status === 401 && !path.startsWith('/auth')) {
      dispatchConsoleSessionExpired()
      throw new Error('console session expired')
    }
    // Non-2xx without the special marker: surface a human-readable
    // message from the body if we can.
    let reason = `HTTP ${response.status}`
    if (isEnvelope(parsed)) {
      const envMsg = extractMessage(parsed)
      if (envMsg) reason = envMsg
    } else if (typeof parsed === 'string' && parsed) {
      reason = parsed
    }
    if (!options?.silent) message.error(reason)
    // Carry the status and envelope code so callers can branch on a specific
    // failure; the message stays the human-readable text it always was.
    const error = new Error(reason) as ApiRequestError
    error.status = response.status
    if (isEnvelope(parsed)) {
      const envelopeCode = parsed.code ?? parsed.Code
      if (envelopeCode !== undefined) error.code = envelopeCode
    }
    throw error
  }

  return handleEnvelope(parsed as T, options?.silent, path)
}

export function apiGet<T>(path: string, options?: ApiRequestOptions): Promise<T> {
  return requestJson<T>('GET', path, undefined, options)
}

export function apiPost<T>(path: string, body?: unknown, options?: ApiRequestOptions): Promise<T> {
  return requestJson<T>('POST', path, body, options)
}

export function apiPut<T>(path: string, body?: unknown, options?: ApiRequestOptions): Promise<T> {
  return requestJson<T>('PUT', path, body, options)
}

export function apiDelete<T>(path: string, options?: ApiRequestOptions): Promise<T> {
  return requestJson<T>('DELETE', path, undefined, options)
}

export function apiPatch<T>(path: string, body?: unknown, options?: ApiRequestOptions): Promise<T> {
  return requestJson<T>('PATCH', path, body, options)
}
